import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, RotateCcw, Trash2 } from "lucide-react";

const TONES = {
    danger: {
        Icon: Trash2,
        iconWrap: "bg-coral-light text-coral",
        confirm:
            "bg-coral text-white hover:bg-[#b8442f] focus-visible:ring-coral/40",
    },
    warning: {
        Icon: RotateCcw,
        iconWrap: "bg-mustard-light text-mustard",
        confirm:
            "bg-mustard text-forest hover:bg-yellow-400 focus-visible:ring-mustard/40",
    },
    default: {
        Icon: AlertTriangle,
        iconWrap: "bg-green-light text-forest",
        confirm:
            "bg-forest text-white hover:bg-[#16291e] focus-visible:ring-forest/30",
    },
};

/**
 * Branded confirmation dialog — a drop-in replacement for window.confirm()
 * that follows the Dorito Stats brand (Fraunces heading, forest/mustard/coral
 * palette) and is accessible (focus trap, ESC to dismiss, overlay click).
 *
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {() => void} props.onConfirm
 * @param {string} props.title
 * @param {React.ReactNode} props.description
 * @param {string} [props.confirmLabel="Confirm"]
 * @param {string} [props.cancelLabel="Cancel"]
 * @param {"danger"|"warning"|"default"} [props.tone="default"]
 * @param {boolean} [props.busy=false] - shows a pending state and locks buttons
 */
const ConfirmDialog = ({
    open,
    onOpenChange,
    onConfirm,
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "default",
    busy = false,
}) => {
    const { Icon, iconWrap, confirm } = TONES[tone] || TONES.default;

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[9998] bg-forest/40 backdrop-blur-[2px]" />
                <Dialog.Content
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className="fixed left-1/2 top-1/2 z-[9999] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border-card bg-surface p-6 shadow-card focus:outline-none"
                >
                    <div className="flex items-start gap-4">
                        <div
                            className={`flex h-11 w-11 flex-none items-center justify-center rounded-full ${iconWrap}`}
                        >
                            <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Dialog.Title className="font-display text-xl font-bold text-ds-text">
                                {title}
                            </Dialog.Title>
                            <Dialog.Description className="mt-1.5 text-sm leading-relaxed text-text-muted">
                                {description}
                            </Dialog.Description>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-2">
                        <Dialog.Close asChild>
                            <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg border border-border px-4 py-2 font-sans text-sm font-bold text-text-mid transition-colors hover:border-ds-text hover:text-ds-text disabled:opacity-50"
                            >
                                {cancelLabel}
                            </button>
                        </Dialog.Close>
                        <button
                            type="button"
                            autoFocus
                            disabled={busy}
                            onClick={onConfirm}
                            className={`rounded-lg px-4 py-2 font-sans text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 disabled:opacity-50 ${confirm}`}
                        >
                            {busy ? "Working…" : confirmLabel}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default ConfirmDialog;
